import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Wrench, CalendarCheck, AlertTriangle, FileText, Factory, CircleDot, ShieldCheck, FileSignature } from "lucide-react";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import { Button, Spinner } from "../components/ui/kit";
import NovyZapisDialog from "../components/NovyZapisDialog";
import NovyPozadavekDialog from "../components/NovyPozadavekDialog";
import FiltrLista, { useFiltr, SkrytoFiltrem, PrazdnyFiltr } from "../components/FiltrLista";
import { odpovida, dnes, plusDni } from "../lib/filtr";
import { poleTechnologie, poleMesto, polePevne } from "../lib/filtryPole";
import { useTechnologie } from "../lib/technologie";
import { fmtMonthYear, initials } from "../lib/servis";
import api from "../lib/api";
import { useUrlStav } from "../lib/navigace";

const TVARY = ["zařízení", "zařízení", "zařízení"];
const ANO_NE = [{ v: "ANO", label: "Má" }, { v: "NE", label: "Nemá" }];
const konecMesice = (d) => { const [y, m] = d.split("-").map(Number); return dnes(new Date(y, m, 0)); };
/** Prohlídka: po termínu, tento měsíc (⊂ do 3 měsíců), bez termínu. */
const prohlidka = (z, d = dnes()) => {
  const p = z.dalsi_prohlidka;
  if (!p) return ["BEZ"];
  if (p < d) return ["PO_TERMINU"];
  const out = p <= plusDni(d, 90) ? ["KVARTAL"] : [];
  return p <= konecMesice(d) ? ["MESIC", ...out] : out;
};
// Starší odkazy (?filtr=nedostatky z Přehledu) → pohled.
const STARY_FILTR = { po_terminu: "po_terminu", nedostatky: "nedostatky" };

export default function Servis() {
  const nav = useNavigate();
  const [stary] = useUrlStav("filtr");
  const [pohled, setPohled] = useUrlStav("pohled", STARY_FILTR[String(stary || "").toLowerCase()] || "vse");
  const [q, setQ] = useUrlStav("q", "");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [novy, setNovy] = useState(false);
  const [pozadavek, setPozadavek] = useState(false);
  const technologie = useTechnologie();

  const load = useCallback(async () => {
    const params = { limit: 1000 };
    if (q) params.q = q;
    const { data } = await api.get("/api/zarizeni", { params });
    setItems(data.items || []);
    setTotal(data.total || 0);
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const katMap = useMemo(() => Object.fromEntries((technologie || []).map((k) => [k.id, k.name])), [technologie]);
  const pole = useMemo(() => [
    polePevne({ klic: "prohlidka", nazev: "Prohlídka", rod: "z", ikona: CalendarCheck, tvary: ["termín", "termíny", "termínů"],
      hodnoty: [{ v: "PO_TERMINU", label: "Po termínu", tecka: "#d93a2b" }, { v: "MESIC", label: "Tento měsíc" },
        { v: "KVARTAL", label: "Do 3 měsíců" }, { v: "BEZ", label: "Bez termínu" }], hodnotyPolozky: (z) => prohlidka(z) }),
    polePevne({ klic: "nedostatky", nazev: "Nedostatky", rod: "m", ikona: AlertTriangle, tvary: ["stav", "stavy", "stavů"],
      hodnoty: [{ v: "OTEVRENE", label: "Otevřené" }, { v: "PO_TERMINU", label: "Po termínu", tecka: "#d93a2b" }],
      hodnotyPolozky: (z) => [...((z.otevrenych_nedostatku || 0) > 0 ? ["OTEVRENE"] : []),
        ...((z.nedostatku_po_terminu || 0) > 0 ? ["PO_TERMINU"] : [])] }),
    polePevne({ klic: "zapis", nazev: "Rozpracovaný zápis", rod: "m", ikona: FileText, tvary: ["možnost", "možnosti", "možností"],
      hodnoty: ANO_NE, hodnotyPolozky: (z) => [z.rozpracovany_zapis ? "ANO" : "NE"] }),
    poleTechnologie((z) => (katMap[z.kategorie_id] ? [katMap[z.kategorie_id]] : [])),
    { ...poleTechnologie((z) => (z.vyrobce ? [z.vyrobce] : [])), klic: "vyrobce", nazev: "Výrobce", rod: "m", ikona: Factory,
      tvary: ["výrobce", "výrobci", "výrobců"] },
    polePevne({ klic: "stav", nazev: "Stav", rod: "m", ikona: CircleDot, tvary: ["stav", "stavy", "stavů"],
      hodnoty: [{ v: "V_PROVOZU", label: "V provozu" }, { v: "MONTAZ", label: "Montáž" }], hodnotyPolozky: (z) => [z.stav || "V_PROVOZU"] }),
    polePevne({ klic: "zaruka", nazev: "Záruka", rod: "z", ikona: ShieldCheck, tvary: ["stav", "stavy", "stavů"],
      hodnoty: [{ v: "V_ZARUCE", label: "V záruce" }, { v: "PO_ZARUCE", label: "Po záruce" }, { v: "BEZ", label: "Bez záruky" }],
      hodnotyPolozky: (z) => [!z.zaruka_do ? "BEZ" : z.zaruka_do >= dnes() ? "V_ZARUCE" : "PO_ZARUCE"] }),
    polePevne({ klic: "smlouva", nazev: "Servisní smlouva", rod: "z", ikona: FileSignature, tvary: ["možnost", "možnosti", "možností"],
      hodnoty: ANO_NE, hodnotyPolozky: (z) => [z.servisni_smlouva ? "ANO" : "NE"] }),
    poleMesto((z) => z.adresa_instalace?.mesto),
  ], [katMap]);
  const [filtry, setFiltry] = useFiltr(pole);
  const vsechny = useMemo(() => items || [], [items]);
  const pohledy = useMemo(() => [
    { klic: "vse", nazev: "Vše", test: () => true },
    { klic: "po_terminu", nazev: "Po termínu", test: (z) => z.po_terminu },
    { klic: "mesic", nazev: "Tento měsíc", test: (z) => !!z.dalsi_prohlidka && z.dalsi_prohlidka <= konecMesice(dnes()) },
    { klic: "nedostatky", nazev: "S nedostatky", test: (z) => (z.otevrenych_nedostatku || 0) > 0 },
  ].map((p) => ({ ...p, pocet: vsechny.filter(p.test).length })), [vsechny]);
  const rozsah = useMemo(() => vsechny.filter((pohledy.find((p) => p.klic === pohled) || pohledy[0]).test), [vsechny, pohledy, pohled]);
  const shown = useMemo(() => rozsah.filter((z) => odpovida(z, filtry, pole)), [rozsah, filtry, pole]);

  return (
    <div data-testid="servis-page">
      <PageHeader eyebrow="Servis" title="Zařízení v péči"
        subtitle="Karty zařízení, servisní intervaly a zápisy z terénu."
        actions={
          <div className="flex gap-2">
            <button onClick={() => setPozadavek(true)} data-testid="servis-novy-pozadavek"
              className="tap md:min-h-0 pill border border-line-strong bg-white px-4 h-10 md:h-9 t-meta font-600 text-ink hover:bg-inputbg inline-flex items-center gap-1.5">
              <Wrench size={15} /> Nový požadavek
            </button>
            <Button onClick={() => setNovy(true)} data-testid="servis-novy-zapis"><Plus size={15} /> Nový zápis</Button>
          </div>
        } />

      <FiltrLista pole={pole} polozky={rozsah} filtry={filtry} setFiltry={setFiltry} tvary={TVARY}
        pohledy={pohledy} pohled={pohled} setPohled={setPohled} vysledek={shown.length} celkem={rozsah.length}
        hledani={{ q, setQ, placeholder: "Hledat zařízení, SN, klienta…", testid: "servis-search" }} testid="servis-filtr" />
      {items && total > items.length && (
        <div className="mb-3 t-meta text-muted" data-testid="servis-orez">Zobrazeno {items.length} z {total} — upřesni hledání.</div>
      )}

      <div className="card overflow-hidden">
        {items === null ? (
          <div className="p-10 flex justify-center"><Spinner /></div>
        ) : vsechny.length === 0 ? (
          <EmptyState icon="Wrench" title={q ? "Nic nenalezeno" : "Zatím žádná zařízení"}
            description={q ? "Zkus jiné hledání." : "Zařízení přidáš na kartě klienta v tabu Servis. Pak k němu můžeš psát zápisy z terénu."} />
        ) : shown.length === 0 ? (
          filtry.length ? <PrazdnyFiltr nazev="Žádné zařízení" onVymazat={() => setFiltry([])} />
            : <EmptyState icon="Wrench" title="V tomhle pohledu nic není" description="Přepni na Vše." />
        ) : shown.map((d) => {
          const my = fmtMonthYear(d.dalsi_prohlidka);
          // Nedostatek s propadlým termínem je červeně — to je slib klientovi,
          // který jsme nesplnili. Štítky: počítač v řádku, telefon pod názvem.
          const stitky = (d.otevrenych_nedostatku > 0 || d.rozpracovany_zapis) ? (
            <>
                {d.otevrenych_nedostatku > 0 && (
                  <span className={`label-up px-2 py-1 rounded-chip shrink-0 ${
                    d.nedostatku_po_terminu > 0
                      ? "bg-danger/10 text-danger"
                      : "bg-[#fdf6e8] text-[#7a4c04]"}`}
                    data-testid={`zarizeni-nedostatky-${d.id}`}>
                    {d.nedostatku_po_terminu > 0
                      ? `${d.nedostatku_po_terminu} po termínu`
                      : `${d.otevrenych_nedostatku} ${d.otevrenych_nedostatku === 1 ? "nedostatek" : "nedostatky"}`}
                  </span>
                )}
                {d.rozpracovany_zapis && (
                  <span className="label-up px-2 py-1 rounded-chip bg-[#fdf6e8] text-[#7a4c04] shrink-0">rozpracováno</span>
                )}
            </>
          ) : null;
          return (
            <button key={d.id} onClick={() => nav(`/servis/zarizeni/${d.id}`)} data-testid={`zarizeni-${d.id}`}
              className="w-full text-left flex items-center gap-3 px-4 md:px-5 py-3.5 border-b border-line-softer last:border-0 hover:bg-inputbg">
              <span className="h-10 w-10 rounded-icon bg-inputbg text-sec font-700 text-[11px] flex items-center justify-center shrink-0">
                {initials(d.client_name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-600 text-ink truncate">{d.nazev}</span>
                <span className="block text-[11.5px] text-muted truncate">
                  {d.client_name || "—"}{d.adresa_instalace?.mesto ? ` — ${d.adresa_instalace.mesto}` : ""}
                </span>
                {stitky && <span className="md:hidden flex flex-wrap items-center gap-1 mt-1.5">{stitky}</span>}
              </span>
              <span className="hidden md:contents">{stitky}</span>
              <span className={`text-[11px] font-600 text-right shrink-0 max-w-[96px] md:max-w-none self-start md:self-center ${d.po_terminu ? "text-danger" : "text-sec"}`}>
                {d.po_terminu ? `po termínu — ${my}` : my ? `prohlídka ${my}` : "bez servisu"}
              </span>
            </button>
          );
        })}
        {shown.length > 0 && <SkrytoFiltrem skryto={filtry.length ? rozsah.length - shown.length : 0} tvary={TVARY} onZobrazit={() => setFiltry([])} />}
      </div>

      {novy && <NovyZapisDialog onClose={() => setNovy(false)} />}
      {pozadavek && <NovyPozadavekDialog onClose={() => setPozadavek(false)} />}
    </div>
  );
}
